import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Loader2, 
  Download, 
  Trash2, 
  FileText, 
  Shield, 
  Clock, 
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { useUserConsentRecords, useRevokeConsent, ConsentRecord } from '@/hooks/useConsent';
import { 
  useDataSubjectRequests, 
  useCreateDataSubjectRequest,
  useCancelDataSubjectRequest,
  useExportUserData,
  downloadDataAsFile,
  _DataSubjectRequest,
} from '@/hooks/useDataSubjectRights';

interface PrivacyCenterProps {
  userId: string;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock className="h-4 w-4 text-yellow-500" />,
  processing: <Loader2 className="h-4 w-4 animate-spin text-blue-500" />,
  completed: <CheckCircle className="h-4 w-4 text-green-500" />,
  failed: <XCircle className="h-4 w-4 text-destructive" />,
  cancelled: <AlertCircle className="h-4 w-4 text-muted-foreground" />,
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

export function PrivacyCenter({ userId }: PrivacyCenterProps) {
  const { t } = useTranslation('common');
  
  // Hooks
  const { data: consentRecords, isLoading: consentsLoading } = useUserConsentRecords(userId);
  const { data: requests, isLoading: requestsLoading } = useDataSubjectRequests(userId);
  const revokeConsentMutation = useRevokeConsent();
  const createRequestMutation = useCreateDataSubjectRequest();
  const cancelRequestMutation = useCancelDataSubjectRequest();
  const exportDataMutation = useExportUserData();

  const [activeTab, setActiveTab] = useState('consents');

  const handleExportData = async () => {
    try {
      const data = await exportDataMutation.mutateAsync(userId);
      downloadDataAsFile(data);
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleRequestDeletion = async () => {
    await createRequestMutation.mutateAsync({
      userId,
      requestType: 'deletion',
      notes: 'User requested account and data deletion',
    });
  };

  const handleCancelRequest = async (requestId: string) => {
    await cancelRequestMutation.mutateAsync({ requestId, userId });
  };

  const handleRevokeConsent = async (record: ConsentRecord) => {
    await revokeConsentMutation.mutateAsync({
      recordId: record.id,
      userId,
      reason: 'User revoked consent',
    });
  };

  const getConsentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      hipaa_authorization: t('consent.types.hipaa', 'HIPAA Authorization'),
      privacy_policy: t('consent.types.privacy', 'Privacy Policy'),
      terms_of_service: t('consent.types.terms', 'Terms of Service'),
      data_processing: t('consent.types.dataProcessing', 'Data Processing'),
    };
    return labels[type] || type;
  };

  const getRequestTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      export: t('privacy.requestTypes.export', 'Data Export'),
      deletion: t('privacy.requestTypes.deletion', 'Account Deletion'),
      access: t('privacy.requestTypes.access', 'Data Access'),
      rectification: t('privacy.requestTypes.rectification', 'Data Correction'),
    };
    return labels[type] || type;
  };

  // Check if there's a pending deletion request
  const hasPendingDeletion = requests?.some(
    r => r.request_type === 'deletion' && r.status === 'pending'
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          {t('privacy.title', 'Privacy Center')}
        </CardTitle>
        <CardDescription>
          {t('privacy.description', 'Manage your privacy settings, view consent history, and exercise your data rights.')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="consents">
              {t('privacy.tabs.consents', 'Consents')}
            </TabsTrigger>
            <TabsTrigger value="dataRights">
              {t('privacy.tabs.dataRights', 'Data Rights')}
            </TabsTrigger>
            <TabsTrigger value="requests">
              {t('privacy.tabs.requests', 'Requests')}
            </TabsTrigger>
          </TabsList>

          {/* Consents Tab */}
          <TabsContent value="consents" className="space-y-4">
            {consentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : consentRecords && consentRecords.length > 0 ? (
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {consentRecords.map((record) => (
                    <div 
                      key={record.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {getConsentTypeLabel(record.consent_type)}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            v{record.consent_version}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {record.revoked_at ? (
                            <span className="text-destructive">
                              {t('privacy.revokedOn', 'Revoked on {{date}}', {
                                date: new Date(record.revoked_at).toLocaleDateString(),
                              })}
                            </span>
                          ) : (
                            t('privacy.consentedOn', 'Consented on {{date}}', {
                              date: new Date(record.consented_at).toLocaleDateString(),
                            })
                          )}
                        </p>
                      </div>
                      {!record.revoked_at && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive">
                              {t('privacy.withdraw', 'Withdraw')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t('privacy.withdrawConsent.title', 'Withdraw Consent?')}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('privacy.withdrawConsent.description', 
                                  'Withdrawing this consent may affect your ability to use certain features of the service.'
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRevokeConsent(record)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {t('privacy.withdrawConsent.confirm', 'Withdraw Consent')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {t('privacy.noConsents', 'No consent records found.')}
              </p>
            )}
          </TabsContent>

          {/* Data Rights Tab */}
          <TabsContent value="dataRights" className="space-y-4">
            <div className="grid gap-4">
              {/* Export Data */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <h4 className="font-medium flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    {t('privacy.exportData.title', 'Export Your Data')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacy.exportData.description', 'Download a copy of all your personal data.')}
                  </p>
                </div>
                <Button 
                  onClick={handleExportData}
                  disabled={exportDataMutation.isPending}
                >
                  {exportDataMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {t('privacy.exportData.button', 'Export')}
                </Button>
              </div>

              <Separator />

              {/* Delete Account */}
              <div className="flex items-center justify-between p-4 border rounded-lg border-destructive/20 bg-destructive/5">
                <div className="space-y-1">
                  <h4 className="font-medium flex items-center gap-2 text-destructive">
                    <Trash2 className="h-4 w-4" />
                    {t('privacy.deleteAccount.title', 'Delete Account')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacy.deleteAccount.description', 'Permanently delete your account and all associated data.')}
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive"
                      disabled={hasPendingDeletion || createRequestMutation.isPending}
                    >
                      {hasPendingDeletion ? (
                        t('privacy.deleteAccount.pending', 'Request Pending')
                      ) : (
                        t('privacy.deleteAccount.button', 'Request Deletion')
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('privacy.deleteAccount.confirmTitle', 'Are you absolutely sure?')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('privacy.deleteAccount.confirmDescription', 
                          'This action cannot be undone. Your account and all associated data will be permanently deleted within 30 days.'
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleRequestDeletion}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {t('privacy.deleteAccount.confirmButton', 'Yes, Delete My Account')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            {requestsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : requests && requests.length > 0 ? (
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {requests.map((request) => (
                    <div 
                      key={request.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {STATUS_ICONS[request.status]}
                          <span className="font-medium">
                            {getRequestTypeLabel(request.request_type)}
                          </span>
                          <Badge className={STATUS_COLORS[request.status]}>
                            {request.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('privacy.requestedOn', 'Requested on {{date}}', {
                            date: new Date(request.requested_at).toLocaleDateString(),
                          })}
                        </p>
                      </div>
                      {request.status === 'pending' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleCancelRequest(request.id)}
                          disabled={cancelRequestMutation.isPending}
                        >
                          {t('common.cancel', 'Cancel')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {t('privacy.noRequests', 'No data subject requests found.')}
              </p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
